import React, { useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import hljs from 'highlight.js';
import 'highlight.js/styles/atom-one-dark.css';

const openCodeInVSCode = async (codeSnippet: string, language: string = 'javascript') => {
    const AUTH_TOKEN = "jIVxNFGzeImEYxK2vjMKCP2m349bqthuDTagDZadqZ87qLwozmaXjUn";

    try {
        const response = await fetch('http://127.0.0.1:4842/open', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${AUTH_TOKEN}`
            },
            body: JSON.stringify({
                content: codeSnippet,
                language: language
            })
        });

        if (!response.ok) {
            throw new Error("Erreur d'authentification avec l'extension");
        }
    } catch (error) {
        console.error(error);
        alert("Impossible de communiquer avec Edu-JALM Helper.");
    }
};

interface ChatMessageContentProps {
    content: string;
    isOwn: boolean;
    decryptionFailed?: boolean;
    isPartiallyEncrypted?: boolean;
    onResend?: () => void;
}

const ChatMessageContent: React.FC<ChatMessageContentProps> = ({ content, isOwn, decryptionFailed, isPartiallyEncrypted, onResend }) => {
    useEffect(() => {
        if (!decryptionFailed) {
            hljs.highlightAll();
        }
    }, [content, decryptionFailed]);

    if (decryptionFailed) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, py: 0.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: isOwn ? 'rgba(255,255,255,0.9)' : 'error.main' }}>
                    <Box component="span" sx={{ fontSize: '1.1rem' }}>⚠️</Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {isOwn ? "Erreur de chiffrement (vos clés ont peut-être changé)" : "Déchiffrement impossible"}
                    </Typography>
                </Box>
                {isOwn && onResend && (
                    <Box sx={{ mt: 0.5 }}>
                        <IconButton 
                            size="small" 
                            onClick={(e) => { e.stopPropagation(); onResend(); }}
                            sx={{ 
                                bgcolor: 'rgba(255,255,255,0.2)', 
                                color: 'white',
                                borderRadius: '4px',
                                px: 1.5,
                                py: 0.5,
                                fontSize: '0.75rem',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' }
                            }}
                        >
                            Renvoyer le message
                        </IconButton>
                    </Box>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ position: 'relative' }}>
            {/* Split content by code blocks: ```language\ncode``` or ```code``` */}
            {content.split(/(```\w*[\s\S]*?```)/g).map((part, index, parts) => {
                const match = part.match(/```(\w*)\n?([\s\S]*?)```/);
                
                if (match) {
                    const language = match[1] || 'plaintext';
                    const code = match[2].trim();

                    return (
                        <Box 
                            key={index} 
                            sx={{ 
                                my: 1.5, 
                                borderRadius: 2, 
                                overflow: 'hidden', 
                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                bgcolor: '#282c34',
                                border: '1px solid',
                                borderColor: isOwn ? 'rgba(255,255,255,0.1)' : 'divider',
                                maxWidth: '100%',
                                minWidth: { xs: '100%', sm: '300px' }
                            }}
                        >
                            <Box sx={{ 
                                bgcolor: '#21252b', 
                                px: 1.5, 
                                py: 0.5, 
                                borderBottom: '1px solid #3e4451', 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center' 
                            }}>
                                <Typography variant="caption" sx={{ color: '#abb2bf', fontFamily: 'monospace', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                    {language}
                                </Typography>
                                <Tooltip title="Ouvrir avec Visual Studio Code" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            openCodeInVSCode(code, language);
                                        }}
                                        sx={{
                                            color: '#abb2bf',
                                            padding: '2px',
                                            '&:hover': {
                                                color: '#61dafb',
                                                bgcolor: 'rgba(97, 218, 251, 0.1)'
                                            },
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M17.583 2.248L12.23 6.6l-4.088-3.104L2.5 5.81v12.38l5.643 2.314L12.23 17.4l5.353 4.352L23.5 19.065V4.935l-5.917-2.687zM8.143 16.676V7.324l9.714 4.676-9.714 4.676z" />
                                        </svg>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                            <pre style={{ margin: 0, overflowX: 'auto' }}>
                                <code className={`language-${language}`} style={{ padding: '12px', display: 'block', fontSize: '0.85rem', lineHeight: 1.4, color: '#abb2bf' }}>
                                    {code}
                                </code>
                            </pre>
                        </Box>
                    );
                }

                // Normal text part
                if (!part.trim() && index !== 0 && index !== parts.length - 1) return null;

                return (
                    <Typography 
                        key={index} 
                        variant="body2" 
                        sx={{ 
                            wordBreak: 'break-word', 
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap'
                        }}
                    >
                        {part}
                    </Typography>
                );
            })}

            {isOwn && onResend && (
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1,
                    opacity: 0.6,
                    '&:hover': { opacity: 1 }
                }}>
                    {isPartiallyEncrypted && (
                        <Tooltip title="Ce message n'est pas lisible par tous les destinataires prévus (ex: superviseur)">
                            <Box sx={{ display: 'flex', alignItems: 'center', color: isOwn ? 'rgba(255,255,255,0.8)' : 'warning.main', gap: 0.5 }}>
                                <Box component="span" sx={{ fontSize: '0.9rem' }}>⚠️</Box>
                                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>Envoi partiel</Typography>
                            </Box>
                        </Tooltip>
                    )}
                    <Tooltip title="Renvoyer le message (utile si le destinataire ne peut pas le lire)">
                        <IconButton 
                            size="small" 
                            onClick={(e) => { e.stopPropagation(); onResend(); }}
                            sx={{ color: isOwn ? 'white' : 'inherit', p: 0.5 }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="23 4 23 10 17 10"></polyline>
                                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
                            </svg>
                        </IconButton>
                    </Tooltip>
                </Box>
            )}
        </Box>
    );
};

export default ChatMessageContent;
